import React, { useEffect, useMemo, useState } from "react";
import {
  DndContext,
  DragOverlay,
  pointerWithin,
  PointerSensor,
  useDroppable,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import WebRTCStream from "./WebRTCStream";
import InverterPanel from "./InverterPanel";
import RecentAlerts from "./RecentAlerts";
import { useAuth } from "../context/AuthContext";
import "./DashboardCameraBoard.css";
import "./WebRTCStream.css";
import './DesktopDashboard.css';

const COL1_BASE = "cameraCol1";
const COL2_BASE = "cameraCol2";

const getUserKey = (token) => {
  if (!token) return "default";
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    if (payload.sub) return String(payload.sub);
    if (payload.user_id) return String(payload.user_id);
  } catch {}
  return token;
};

const itemId = (colId, streamId) => `cam-${colId}-${streamId}`;

const findInColumns = (id, col1, col2) => {
  for (const [colId, list] of [[1, col1], [2, col2]]) {
    const index = list.findIndex((s) => itemId(colId, s.id) === id);
    if (index !== -1) return { colId, index, stream: list[index] };
  }
  return null;
};

const resolveTarget = (overId, col1, col2) => {
  if (overId === "col-1" || overId === "col-2") {
    const colId = Number(overId.split("-")[1]);
    const list = colId === 1 ? col1 : col2;
    return { colId, index: list.length };
  }
  for (const [colId, list] of [[1, col1], [2, col2]]) {
    const index = list.findIndex((s) => itemId(colId, s.id) === overId);
    if (index !== -1) return { colId, index };
  }
  return null;
};

const SortableCamera = ({ stream, columnId, handleStreamError, onStatusChange, focusedId, onFocus }) => {
  const id = itemId(columnId, stream.id);
  const draggedRef = React.useRef(false);
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id,
    data: { columnId, streamId: stream.id },
  });

  useEffect(() => { if (isDragging) draggedRef.current = true; }, [isDragging]);
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      className={`stream-card ${isDragging ? "dragging" : ""} ${focusedId === stream.id ? 'desktop-focused-camera' : ''}`}
      onClick={() => { if (!draggedRef.current) onFocus(stream.id); draggedRef.current = false; }}
      onKeyDown={(event) => { if (event.target === event.currentTarget && (event.key === 'Enter' || event.key === ' ')) { event.preventDefault(); onFocus(stream.id); } }}
      style={style}
      data-column={columnId}
      {...attributes}
      {...listeners}
    >
      <div className="stream-player">
        <WebRTCStream
          streamId={stream.id}
          streamName={stream.name}
          streamUrl={stream.url}
          onError={(err) => handleStreamError(stream.id, err)}
          showControls={false}
          dashboard
          onStatusChange={onStatusChange}
        />
      </div>
    </div>
  );
};

const CameraColumn = ({ columnId, streams, handleStreamError, onStatusChange, focusedId, onFocus }) => {
  const { setNodeRef, isOver } = useDroppable({
    id: `col-${columnId}`,
  });
  const itemIds = useMemo(
    () => streams.map((s) => itemId(columnId, s.id)),
    [streams, columnId]
  );

  return (
    <div
      ref={setNodeRef}
      className={`camera-column ${isOver ? "drag-over" : ""} ${
        streams.length === 0 ? "empty" : ""
      }`}
      data-column={columnId}
    >
      <SortableContext items={itemIds} strategy={verticalListSortingStrategy}>
        {streams.map((stream) => (
          <SortableCamera
            key={itemId(columnId, stream.id)}
            stream={stream}
            columnId={columnId}
            handleStreamError={handleStreamError}
            onStatusChange={onStatusChange}
            focusedId={focusedId}
            onFocus={onFocus}
          />
        ))}
      </SortableContext>
      {streams.length === 0 && (
        <div className="column-placeholder">Drop here</div>
      )}
    </div>
  );
};

const CameraDropZone = ({ isActive }) => {
  const { setNodeRef, isOver } = useDroppable({
    id: "col-2",
    disabled: !isActive,
  });

  return (
    <div
      ref={setNodeRef}
      className={`camera-drop-zone ${isOver ? "drag-over" : ""} ${
        isActive ? "active" : "hidden"
      }`}
    >
      <span>Drop here</span>
    </div>
  );
};

const DashboardTab = ({ streams = [], loading = false, error = null, onNavigate, onRecentCount, onSystemStatus }) => {
  const [mode, setMode] = useState('grid');
  const [focusedId, setFocusedId] = useState(null);
  const [statuses, setStatuses] = useState({});
  const [inverterStatus, setInverterStatus] = useState('unknown');
  const onStatusChange = React.useCallback((id, status) => setStatuses((prev) => prev[id] === status ? prev : { ...prev, [id]: status }), []);
  const liveCount = streams.filter((s) => statuses[s.id] === 'live').length;
  useEffect(() => {
    onSystemStatus?.(`${liveCount} of ${streams.length} cameras live · Inverter ${inverterStatus}`);
  }, [liveCount, streams.length, inverterStatus, onSystemStatus]);
  const selectFocus = (id) => {
    if (!window.matchMedia('(min-width: 1024px)').matches) return;
    setFocusedId(id);
    setMode('focus');
  };
  const { token } = useAuth();
  const userKey = getUserKey(token);
  const col1Key = `${COL1_BASE}-${userKey}`;
  const col2Key = `${COL2_BASE}-${userKey}`;

  const [col1, setCol1] = useState([]);
  const [col2, setCol2] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [hasLoaded, setHasLoaded] = useState(false);

  useEffect(() => {
    setHasLoaded(false);
    if (loading) return;
    const saved1 = localStorage.getItem(col1Key)
      ? JSON.parse(localStorage.getItem(col1Key))
      : [];
    const saved2 = localStorage.getItem(col2Key)
      ? JSON.parse(localStorage.getItem(col2Key))
      : [];
    const byId = new Map(streams.map((s) => [s.id, s]));
    const resolve = (ids) => ids.map((id) => byId.get(id)).filter(Boolean);
    const known = new Set([...saved1, ...saved2]);
    const rest = streams.filter((s) => !known.has(s.id));
    setCol1([...resolve(saved1), ...rest]);
    setCol2(resolve(saved2));
    setHasLoaded(true);
  }, [loading, streams, col1Key, col2Key]);

  useEffect(() => {
    if (!hasLoaded) return;
    localStorage.setItem(col1Key, JSON.stringify(col1.map((s) => s.id)));
    localStorage.setItem(col2Key, JSON.stringify(col2.map((s) => s.id)));
  }, [col1, col2, col1Key, col2Key, hasLoaded]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        delay: 500,
        tolerance: 5,
      },
    })
  );

  const handleStreamError = (streamId, err) => {
    console.error(`Stream ${streamId} error:`, err);
  };

  const activeStream = useMemo(() => {
    if (!activeId) return null;
    const found = findInColumns(activeId, col1, col2);
    return found ? found.stream : null;
  }, [activeId, col1, col2]);

  const handleDragStart = (event) => {
    setActiveId(event.active.id);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    setActiveId(null);
    if (!over) return;

    const source = findInColumns(active.id, col1, col2);
    const target = resolveTarget(over.id, col1, col2);
    if (!source || !target) return;

    if (source.colId === target.colId) {
      if (source.index === target.index) return;
      const list = source.colId === 1 ? [...col1] : [...col2];
      const moved = arrayMove(list, source.index, target.index);
      if (source.colId === 1) setCol1(moved);
      else setCol2(moved);
    } else {
      const sourceList = source.colId === 1 ? [...col1] : [...col2];
      const targetList = target.colId === 1 ? [...col1] : [...col2];
      const [moved] = sourceList.splice(source.index, 1);
      targetList.splice(target.index, 0, moved);
      if (source.colId === 1) {
        setCol1(sourceList);
        setCol2(targetList);
      } else {
        setCol2(sourceList);
        setCol1(targetList);
      }
    }
  };

  if (loading) {
    return (
      <div className="tab-content">
        <div className="tab-loading">Loading streams...</div>
      </div>
    );
  }

  const hasCameras = col1.length > 0 || col2.length > 0;

  return (
    <div className={`tab-content dashboard-desktop-layout desktop-mode-${mode}`}>
      <aside className="dashboard-right-column"><InverterPanel onStatusChange={setInverterStatus} /><RecentAlerts onRecentCount={onRecentCount} /></aside>
      <section className="dashboard-camera-section">
      <div className="desktop-camera-toolbar"><span>CAMERAS <b>{liveCount} live</b></span><div><button className={mode === 'grid' ? 'active' : ''} onClick={() => setMode('grid')}>Grid</button><button className={mode === 'focus' ? 'active' : ''} onClick={() => { setFocusedId(focusedId || streams[0]?.id); setMode('focus'); }}>Focus</button><button onClick={() => onNavigate?.('cameras')}>All cameras</button></div></div>

      {error && <div className="tab-error">{error}</div>}

      {!hasCameras && !loading && (
        <div className="tab-empty">No streams configured.</div>
      )}

      {hasCameras && (
        <DndContext
          sensors={sensors}
          collisionDetection={pointerWithin}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          <div
            className={`camera-board ${
              col2.length ? "two-cols" : "one-col"
            } ${activeId !== null && col2.length === 0 ? "dragging" : ""}`}
          >
            <CameraColumn
              columnId={1}
              streams={col1}
              handleStreamError={handleStreamError}
              onStatusChange={onStatusChange}
              focusedId={focusedId}
              onFocus={selectFocus}
            />
            {col2.length > 0 ? (
              <CameraColumn
                columnId={2}
                streams={col2}
                handleStreamError={handleStreamError}
                onStatusChange={onStatusChange}
                focusedId={focusedId}
                onFocus={selectFocus}
              />
            ) : (
              <CameraDropZone isActive={activeId !== null} />
            )}
          </div>
          <DragOverlay style={{ pointerEvents: "none" }}>
            {activeStream ? (
              <div className="stream-card drag-overlay">
                <span className="drag-overlay-name">{activeStream.name}</span>
              </div>
            ) : null}
          </DragOverlay>
        </DndContext>
      )}
      <p className="desktop-camera-help">Click a camera to focus · Hold and drag to reorder</p>
      </section>
    </div>
  );
};

export default DashboardTab;
